import classNames from 'classnames';
import { useCreateLike, useGetLabels } from '@bpm-web-app/swr-hooks';
import { CreateCardCarouselItem, appendQueryParams, convertToPluralIfNeeded, createAppRoutes, useCreateFilterParams, useViewport } from '@bpm-web-app/utils';
import { Fragment, useMemo, useState } from 'react';
import { Genre } from '@bpm-web-app/create-api-sdk';
import CardGrid from '../../shared/card-grid/card-grid';
import CardCreate from '../../shared/card/card-create';
import Banner from '../../shared/banner/banner';
import PrimaryPageTitle from '../../shared/primary-page-title/primary-page-title';
import styles from './labels.module.css';
import { ClickableGenres } from '../../clickable-genres/clickable-genres';
import { GhostComponent } from '../../shared';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface LabelsProps { }

export function Labels(props: LabelsProps) {
    const { isMobile } = useViewport();
    const { type, synth } = useCreateFilterParams();
    const { data: labels, isLoading } = useGetLabels(false, type, synth);
    const [isBannersLoaded, setIsBannersLoaded] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const { isLiked, likeDislike } = useCreateLike('label');

    const isEverythingLoaded = useMemo(() => {
        if (!isLoading &&
            isBannersLoaded
        ) return true;
        return false;
    }, [isBannersLoaded, isLoading]);

    const filteredLabels = useMemo(() => labels?.filter((label) => label.name.toLowerCase().includes(searchQuery.toLowerCase())) || [], [labels, searchQuery]);

    const getDescriptionMarkup = (packCount: number, genres: Genre[]) => (
        <>
            <div className={styles['labels__packs']}>{convertToPluralIfNeeded(packCount, 'Pack')}</div>
            <ClickableGenres genres={genres} />
        </>
    );

    return (
        // eslint-disable-next-line @typescript-eslint/dot-notation
        <section className={styles['labels']}>
            <div className={styles['labels__reversed-header']}>
                <div className={classNames(styles['labels__header'])}>
                    <PrimaryPageTitle title="Labels" noPadding />
                    <div
                        className={classNames(styles['labels__search'], {
                            [styles['labels__search--has-value']]: !!searchQuery,
                        })}
                    >
                        <input type="text" onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search Labels" value={searchQuery} className={styles['labels__search-input']} />
                        {!!searchQuery && (
                            <button type="button" onClick={() => setSearchQuery('')} className={styles['labels__search-clear']}>
                                <i className="icon icon--controls-clear" />
                            </button>
                        )}
                    </div>
                </div>
                <Banner variant="hidden" platform="create" location="labels" isDataLoaded={isEverythingLoaded} setIsDataLoaded={setIsBannersLoaded} />
            </div>

            {isEverythingLoaded ? (
                <div className={styles['labels__grid-container']}>
                    <CardGrid cardType={isMobile ? 'card-label' : 'card-large'}>
                        {filteredLabels.map((label) => (
                            <Fragment key={label.id}>
                                <CardCreate
                                    contentType="label"
                                    cardSize={isMobile ? 'label' : 'large'}
                                    key={label.id}
                                    id={label.id}
                                    title={label.name}
                                    link={createAppRoutes.labelDetail(label.name)}
                                    imageUrl={appendQueryParams(label.logo_url, { key: 'dw', value: 400 })}
                                    imageUrl2x={appendQueryParams(label.logo_url, { key: 'dw', value: 420 })}
                                    description={getDescriptionMarkup(label.soundpackage_count!, label.top_genres)}
                                    onFavorite={likeDislike}
                                    isFavorite={isLiked(label.id)}
                                    items={filteredLabels as unknown as CreateCardCarouselItem[]}
                                />
                            </Fragment>
                        ))}
                    </CardGrid>
                </div>
            ) : (
                <div className={classNames('spacing--horizontal')}><GhostComponent type="cards" cardSize="large" elementsCount={12} grid /></div>
            )}
        </section>
    );
}
